import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { extractUserFromJWT } from '../utils/jwt';
import { createLDUser } from './context';
import {
  useFeatureFlagFromContext,
  useFeatureFlagsFromContext,
} from './FeatureFlagContext';

/**
 * Hook to access all LaunchDarkly feature flags
 * @returns {Object} All feature flags
 * @deprecated Use the context-based version for better performance
 */
export const useFeatureFlags = () => {
  // For backward compatibility, we still provide the original implementation
  // But we log a warning in development to encourage migration
  if (process.env.NODE_ENV === 'development') {
    console.warn(
      'Direct LaunchDarkly hook calls are deprecated. ' +
        'Use the context-based version for better performance. ' +
        'This will be removed in a future version.'
    );
  }

  // Always call both hooks to avoid conditional hook calls
  const contextFlags = useFeatureFlagsFromContext();
  const ldFlags = useFlags();

  // Return context flags if available, otherwise fall back to direct LaunchDarkly flags
  return contextFlags && Object.keys(contextFlags).length > 0
    ? contextFlags
    : ldFlags;
};

/**
 * Hook to access a specific LaunchDarkly feature flag
 * @param {string} flagKey - The key of the feature flag to access
 * @param {any} defaultValue - The default value to return if the flag is not found
 * @returns {any} The value of the feature flag
 * @deprecated Use the context-based version for better performance
 */
export const useFeatureFlag = (flagKey, defaultValue = false) => {
  // For backward compatibility, we still provide the original implementation
  // But we log a warning in development to encourage migration
  if (process.env.NODE_ENV === 'development') {
    console.warn(
      'Direct LaunchDarkly hook calls are deprecated. ' +
        'Use the context-based version for better performance. ' +
        'This will be removed in a future version.'
    );
  }

  // Always call both hooks to avoid conditional hook calls
  const contextFlag = useFeatureFlagFromContext(flagKey, defaultValue);
  const ldFlags = useFlags();

  // Return context flag if available (not null), otherwise fall back to direct LaunchDarkly flag
  return contextFlag !== null
    ? contextFlag
    : ldFlags[flagKey] !== undefined
      ? ldFlags[flagKey]
      : defaultValue;
};

/**
 * Hook to access the LaunchDarkly client
 * Useful for identifying users or manually evaluating flags
 * @returns {Object} The LaunchDarkly client
 */
export const useLaunchDarkly = () => {
  const client = useLDClient();

  // Expose client to window object for debugging
  if (typeof window !== 'undefined' && client) {
    window.ldClient = client;

    // Create a wrapper to hold the client
    window.LD_WRAPPER = window.LD_WRAPPER || {};
    window.LD_WRAPPER.client = client;

    // Make sure the debug functions are available
    if (!window.listAllFlags && typeof window.initLDDebugTools === 'function') {
      window.initLDDebugTools();
    }
  }

  /**
   * Identify a user to LaunchDarkly
   * @param {Object} user - The user object to identify
   * @returns {Promise} A promise that resolves when the identification is complete
   */
  const identify = async user => {
    if (!client) return Promise.reject('LaunchDarkly client not initialized');
    return client.identify(user);
  };

  /**
   * Manually evaluate a feature flag
   * @param {string} flagKey - The key of the feature flag to evaluate
   * @param {any} defaultValue - The default value to return if the flag is not found
   * @returns {any} The value of the feature flag
   */
  const evaluateFlag = (flagKey, defaultValue = false) => {
    if (!client) return defaultValue;
    return client.variation(flagKey, defaultValue);
  };

  /**
   * Identify the current user from JWT token
   * @returns {Promise} A promise that resolves when the identification is complete
   */
  const identifyFromJWT = async () => {
    if (!client) return Promise.reject('LaunchDarkly client not initialized');

    const accessToken = localStorage.getItem('access');
    if (!accessToken) return Promise.reject('No access token available');

    // Extract user info from JWT
    const userInfo = extractUserFromJWT(accessToken);

    // Create LaunchDarkly user object
    const ldUser = createLDUser({
      userId: userInfo.userId,
      email: userInfo.email,
      name: userInfo.name,
    });

    // Identify the user
    return client.identify(ldUser);
  };

  return {
    client,
    identify,
    identifyFromJWT,
    evaluateFlag,
  };
};
